import { gsap } from "gsap";
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
import Flip from "gsap/Flip";
gsap.registerPlugin(ScrollTrigger, SplitText, Flip);

import flipbutton from '../snippets/flipbutton'
import { onWidthResize, offWidthResize } from '../utils/resize.js';
import { enableTiltHover } from '../utils/tilt-hover.js';

export default class FeaturedCategories {
    constructor(container) {
        this.container = container;
        this.title = this.container.querySelector('.featured-categories__title');
        this.grid = this.container.querySelector('.featured-categories__grid');
        this.product_cards = this.container.querySelectorAll('.featured-categories__box');
        this.flipbuttons = this.container.querySelectorAll('.js-flipbutton');

        this.init();
    }

    init = () => {
        // Mobile / small screens: skip the intro animation and render the final
        // grid as-is. Only wire up the hover flip-buttons.
        if (window.matchMedia('(max-width: 1023px)').matches) {
            this.flipbuttons.forEach(element => {
                new flipbutton(element, { fade: false });
            });
            return;
        }

        if (this.title) {
            this.text_lines = new SplitText(this.title, { type: 'lines,words', linesClass: "lineParent" });
        }
        const state = Flip.getState([this.product_cards, this.grid]);

        // Reserve the grid's natural height before the cards collapse to absolute
        // positioning below. This keeps the document height constant so the page
        // doesn't shift/jump under the user when the intro finishes and the cards
        // return to normal flow.
        this.grid.style.minHeight = `${this.grid.offsetHeight}px`;

        this.product_cards.forEach(element => {
            element.classList.add('centered');
        });

        // Stack and animation both use 200px below the title. Align the top of
        // the deck to that point (not the center) so the cards stay inside the
        // grid and aren't clipped by the section's overflow:hidden.
        const offsetBelowTitlePx = 200;
        let stackTop = offsetBelowTitlePx;
        if (this.title) {
            stackTop = Math.max(
                0,
                this.title.getBoundingClientRect().bottom + offsetBelowTitlePx - this.grid.getBoundingClientRect().top
            );
        }

        gsap.set(this.product_cards, {
            xPercent: -50,
            y: 0,
            yPercent: 0,
            left: "50%",
            top: stackTop,
            transformOrigin: '50% 100%'
        });

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: this.title || this.grid,
                // Start when 200px below the title first enters the viewport —
                // not when the stacked cards reach the viewport center.
                start: this.title ? `bottom+=${offsetBelowTitlePx}px bottom` : `top+=${offsetBelowTitlePx}px bottom`,
                once: true,
            },
        });
        if (this.text_lines) {
            tl.fromTo(this.text_lines.words,
                { yPercent: 100, }, {
                yPercent: 0, duration: 0.6, stagger: { each: 0.1, ease: 'power.out', from: 'start' }, ease: 'power3.out',
                onComplete: () => { onWidthResize(this.onResize); }
            });
        }

        const totalCards = this.product_cards.length;
        // Cards already exist in their centered/stacked state (set above); fan
        // them out in place instead of sliding the whole grid up from below.
        tl.fromTo(this.product_cards, { rotation: 0, }, { rotation: (i) => { const maxRotation = 10; const center = (totalCards - 1) / 2; return ((i - center) / center) * maxRotation; }, transformOrigin: "50% 100%", });

        tl.call(() => {
            Flip.to(state, {
                duration: 0.8,
                ease: "power2.inOut",
                delay: 0,
                //absolute: true,
                stagger: { each: 0.2, from: 'start' },
                onComplete: () => {
                    gsap.set(this.grid, { clearProps: true });
                    // Release the reserved height; the cards now fill the grid at
                    // the same height, so this stays visually seamless.
                    this.grid.style.minHeight = '';
                    this.product_cards.forEach(element => {
                        element.classList.remove('centered');
                        gsap.set(element, { clearProps: true });
                    });
                    // Only once the cards sit in their final grid spots: the
                    // same pointer-following tilt as the blog cards, minus
                    // the read-more label.
                    this.initHover();
                }
            });
        }, null, '>')


        this.flipbuttons.forEach(element => {
            new flipbutton(element, { fade: false });
        });
    }


    initHover = () => {
        this.product_cards.forEach((card) => {
            const photo = card.querySelector('.featured-categories__box-image .image');
            if (photo) enableTiltHover({ area: card, photo });
        });
    }

    onResize = () => {
        this.text_lines?.revert();
        offWidthResize(this.onResize);
    }
}
